import type { Principal, Role } from '../../../commonTypes.tsx';

import type { useUpdateContentDirectRestrictionsMutation$variables as MutationVariables } from './__generated__/useUpdateContentDirectRestrictionsMutation.graphql.ts';

export type ConfluenceDirectRestrictionPrincipalType = 'USER' | 'GROUP';
export type ConfluenceDirectRestrictionPermission = 'view' | 'edit';

function mapPrincipalTypeToDirectRestrictionPrincipalType(
	principalType: Principal['type'],
): ConfluenceDirectRestrictionPrincipalType {
	switch (principalType) {
		case 'group':
		case 'team':
			return 'GROUP';
		case 'user':
		case 'external_user':
		default:
			return 'USER';
	}
}

function roleToPermission(role: Role): ConfluenceDirectRestrictionPermission | null {
	switch (role) {
		case 'can-view':
			return 'view';
		case 'can-edit':
			return 'edit';
		case 'no-access':
			return null;
		default:
			return null;
	}
}

export function buildUpdateContentDirectRestrictionsBulkRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principals,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principals: Principal[];
}): MutationVariables {
	const restrictions = principals.map((principal) => {
		const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
		return { principalId: principal.id, principalType } as const;
	});

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			remove: {
				view: { restrictions },
				edit: { restrictions },
			},
		},
	};
}

export function buildUpdateContentDirectRestrictionsRemoveVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	return {
		cloudId,
		input: {
			contentId,
			includeInvites,
			remove: {
				view: { restrictions: [restriction] },
				edit: { restrictions: [restriction] },
			},
		},
	};
}

export function buildUpdateContentDirectRestrictionsVariables({
	cloudId,
	contentId,
	includeInvites,
	principal,
	previousRole,
	nextRole,
}: {
	cloudId: string;
	contentId: string;
	includeInvites: boolean;
	principal: Principal;
	previousRole: Role;
	nextRole: Role;
}): MutationVariables {
	const principalType = mapPrincipalTypeToDirectRestrictionPrincipalType(principal.type);
	const previousPermission = roleToPermission(previousRole);
	const nextPermission = roleToPermission(nextRole);

	const restriction = {
		principalId: principal.id,
		principalType,
	} as const;

	const add: NonNullable<MutationVariables['input']['add']> = {};
	const remove: NonNullable<MutationVariables['input']['remove']> = {};

	// If they previously had a direct restriction, remove it from that bucket.
	if (previousPermission) {
		remove[previousPermission] = { restrictions: [restriction] };
	}

	// If they will have a direct restriction, add it to the new bucket.
	if (nextPermission) {
		add[nextPermission] = { restrictions: [restriction] };
	}

	// Omit empty objects to avoid sending `{ add: {} }` etc.
	const input: MutationVariables['input'] = {
		contentId,
		includeInvites,
		...(Object.keys(add).length ? { add } : {}),
		...(Object.keys(remove).length ? { remove } : {}),
	};

	return {
		cloudId,
		input,
	};
}
